import * as React from 'react'
import {
    Box,
    Button,
    Checkbox,
    Chip,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    Typography
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'

import useAutoTranslate from '../hooks/useAutoTranslate'
import { normalizeCommandersCastellansCrafting } from '../utils/botSettings'

const COMMANDER_ID_OPTIONS = Array.from({ length: 350 }, (_, index) => String(index + 1))
const CASTELLAN_ID_OPTIONS = Array.from({ length: 50 }, (_, index) => String(index + 1))
const GENERIC_CASTELLAN_LABEL_PREFIX = 'castellan'

const COMMANDER_EFFECTS = [
    { key: 'playerEffects', label: 'Player effects' },
    { key: 'npcEffects', label: 'NPC effects' },
    { key: 'meleeUnitsStrength', label: 'Melee units strength', supportsHighestValue: true },
    { key: 'rangeUnitsStrength', label: 'Range units strength', supportsHighestValue: true },
    { key: 'courtyardStrength', label: 'Courtyard strength', supportsHighestValue: true },
    { key: 'frontUnitsLimit', label: 'Front units limit', supportsHighestValue: true },
    { key: 'wallUnitsLimit', label: 'Flank units limit', supportsHighestValue: true },
    { key: 'travelSpeed', label: 'Travel speed', supportsHighestValue: true },
    { key: 'laterArmyDetection', label: 'Later army detection', supportsHighestValue: true },
    { key: 'frontStrength', label: 'Front strength', supportsHighestValue: true },
    { key: 'flanksStrength', label: 'Flanks strength', supportsHighestValue: true },
    { key: 'universalCombatStrength', label: 'Universal combat strength', supportsHighestValue: true },
    { key: 'mustHaveShieldMaidenSupportInCourtyard', label: 'Must have Shield-maiden support in courtyard' },
    { key: 'mustHaveAdditionalWaves', label: 'Must have additional waves' }
]

const CASTELLAN_EFFECTS = [
    { key: 'playerEffects', label: 'Player effects' },
    { key: 'npcEffects', label: 'NPC effects' },
    { key: 'meleeUnitsStrength', label: 'Melee units strength', supportsHighestValue: true },
    { key: 'rangeUnitsStrength', label: 'Range units strength', supportsHighestValue: true },
    { key: 'courtyardStrength', label: 'Courtyard strength', supportsHighestValue: true },
    { key: 'wallUnitsLimit', label: 'Wall units limit', supportsHighestValue: true },
    { key: 'defendingMainCastleStrength', label: 'Defending main castle strength', supportsHighestValue: true },
    { key: 'earlierArmyDetection', label: 'Earlier army detection', supportsHighestValue: true },
    { key: 'defendingOutpostLandmarkStrength', label: 'Defending outpost/landmark strength', supportsHighestValue: true },
    { key: 'frontStrength', label: 'Front strength', supportsHighestValue: true },
    { key: 'flanksStrength', label: 'Flanks strength', supportsHighestValue: true },
    { key: 'universalCombatStrength', label: 'Universal combat strength', supportsHighestValue: true },
    { key: 'mustHaveShieldMaidenSupportInCourtyard', label: 'Must have Shield-maiden support in courtyard' }
]

const cloneValue = value =>
    typeof structuredClone === 'function' ?
        structuredClone(value) :
        JSON.parse(JSON.stringify(value ?? {}))

const normalizeCastleTargetLabel = target => {
    const label = String(target?.label ?? target?.nameCandidate ?? '').trim()
    if (!label)
        return ''

    const detail = String(target?.detail ?? '').trim()
    if (!detail || label.includes(detail))
        return label

    return `${label} (${detail})`
}

const isGenericCastellanOptionLabel = (label, value) => {
    const normalizedLabel = String(label ?? '').trim().toLowerCase()
    const normalizedValue = String(value ?? '').trim().toLowerCase()

    if (!normalizedLabel)
        return true

    return normalizedLabel === GENERIC_CASTELLAN_LABEL_PREFIX ||
        normalizedLabel === `${GENERIC_CASTELLAN_LABEL_PREFIX} ${normalizedValue}`
}

export function buildCastellanCastleFallbackOptions(availableCastleTargets) {
    return (Array.isArray(availableCastleTargets) ? availableCastleTargets : [])
        .map((target, index) => {
            const locationLabel = normalizeCastleTargetLabel(target)
            if (!locationLabel)
                return null

            const value = String(index + 1)
            return {
                value,
                label: `${value}. ${locationLabel}`
            }
        })
        .filter(Boolean)
}

export function mergeCastellanOptionsWithCastleTargets(liveOptions, availableCastleTargets) {
    const fallbackOptions = buildCastellanCastleFallbackOptions(availableCastleTargets)
    const fallbackLabelsByValue = new Map(
        fallbackOptions.map(option => [option.value, option.label])
    )
    const normalizedLiveOptions = (Array.isArray(liveOptions) ? liveOptions : [])
        .map(option => {
            const value = String(option?.selectedId ?? option?.value ?? '').trim()
            if (!value)
                return null

            return {
                value,
                label: String(option?.label ?? option?.name ?? '').trim()
            }
        })
        .filter(Boolean)

    if (normalizedLiveOptions.length === 0)
        return fallbackOptions

    return normalizedLiveOptions.map(option => {
        const fallbackLabel = fallbackLabelsByValue.get(option.value)
        if (!fallbackLabel || !isGenericCastellanOptionLabel(option.label, option.value))
            return option

        return {
            ...option,
            label: fallbackLabel
        }
    })
}

function SectionHeader({ title, description }) {
    return (
        <Stack spacing={0.55}>
            <Typography variant="h5" sx={{ fontSize: '1rem', fontWeight: 800 }}>
                {title}
            </Typography>
            {description && (
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                    {description}
                </Typography>
            )}
        </Stack>
    )
}

function normalizeSelectionOptions(options, selectedId, optionLabelPrefix) {
    const normalizedOptions = (Array.isArray(options) ? options : [])
        .map(option => {
            if (option && typeof option === 'object') {
                const value = String(option.value ?? option.selectedId ?? option.id ?? '').trim()
                if (!value)
                    return null
                const label = String(option.label ?? option.name ?? '').trim()

                return {
                    value,
                    label: label || `${optionLabelPrefix} ${value}`
                }
            }

            const value = String(option ?? '').trim()
            return value ? {
                value,
                label: `${optionLabelPrefix} ${value}`
            } : null
        })
        .filter(Boolean)

    if (selectedId && !normalizedOptions.some(option => option.value === selectedId)) {
        normalizedOptions.unshift({
            value: selectedId,
            label: `${optionLabelPrefix} ${selectedId}`
        })
    }

    return normalizedOptions.length > 0 ? normalizedOptions : [{
        value: selectedId || '1',
        label: `${optionLabelPrefix} ${selectedId || '1'}`
    }]
}

function ScopeCard({ title, description, enabled, selectedId, onToggle, onSelectChange, idLabel, optionLabelPrefix, enabledLabel, options }) {
    const selectionOptions = normalizeSelectionOptions(options, selectedId, optionLabelPrefix)

    return (
        <Paper
            sx={{
                p: 2.2,
                borderRadius: 2.6,
                backgroundColor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                height: '100%'
            }}
        >
            <Stack spacing={2}>
                <SectionHeader title={title} description={description} />

                <Stack direction="row" spacing={1.25} alignItems="center">
                    <Switch checked={enabled} onChange={event => onToggle(event.target.checked)} />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {enabledLabel}
                    </Typography>
                </Stack>

                <FormControl fullWidth size="small">
                    <InputLabel>{idLabel}</InputLabel>
                    <Select
                        value={selectedId}
                        label={idLabel}
                        disabled={!enabled}
                        onChange={event => onSelectChange(String(event.target.value))}
                        sx={{ backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 1.8 }}
                    >
                        {selectionOptions.map(option => (
                            <MenuItem key={`${title}-${option.value}`} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>
        </Paper>
    )
}

function EffectMatrix({
    title,
    description,
    rows,
    scopeEffects,
    onToggleEffect,
    onToggleHighest,
    t
}) {
    return (
        <Paper
            sx={{
                p: 2.2,
                borderRadius: 2.6,
                backgroundColor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                height: '100%'
            }}
        >
            <Stack spacing={2}>
                <SectionHeader title={title} description={description} />

                <Grid
                    container
                    sx={{
                        px: 0.4,
                        pb: 0.6,
                        color: 'text.secondary',
                        display: { xs: 'none', sm: 'flex' }
                    }}
                >
                    <Grid item sm={7}>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            {t('Priority')}
                        </Typography>
                    </Grid>
                    <Grid item sm={2} sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            {t('Use')}
                        </Typography>
                    </Grid>
                    <Grid item sm={3} sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            {t('Highest Value')}
                        </Typography>
                    </Grid>
                </Grid>

                <Stack spacing={0.2}>
                    {rows.map((row, index) => {
                        const effectValue = scopeEffects[row.key]
                        const isToggleObject = row.supportsHighestValue
                        const checked = isToggleObject ? Boolean(effectValue?.enabled) : Boolean(effectValue)
                        const highestChecked = isToggleObject ? Boolean(effectValue?.highestValue) : false

                        return (
                            <Box
                                key={`${title}-${row.key}`}
                                sx={{
                                    py: 1,
                                    borderTop: index === 0 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                                    borderBottom: '1px solid rgba(255,255,255,0.06)'
                                }}
                            >
                                <Grid container spacing={1} alignItems="center">
                                    <Grid item xs={12} sm={7}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.45 }}>
                                            {t(row.label)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={2} sx={{ textAlign: 'center' }}>
                                        <Checkbox
                                            checked={checked}
                                            onChange={event => onToggleEffect(row.key, event.target.checked)}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={3} sx={{ textAlign: 'center' }}>
                                        {row.supportsHighestValue ? (
                                            <Checkbox
                                                checked={highestChecked}
                                                disabled={!checked}
                                                onChange={event => onToggleHighest(row.key, event.target.checked)}
                                                size="small"
                                            />
                                        ) : (
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                {t('Not used for this rule')}
                                            </Typography>
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>
                        )
                    })}
                </Stack>
            </Stack>
        </Paper>
    )
}

export default function CommandersCastellansCraftingPanel({
    value,
    onChange,
    onManualStart,
    appLanguage = 'en',
    botRunning = false,
    botStandbyActive = false,
    botStandbyReason = '',
    runtimeStatus,
    availableCastleTargets
}) {
    const config = React.useMemo(
        () => normalizeCommandersCastellansCrafting(value?.commandersCastellansCrafting),
        [value?.commandersCastellansCrafting]
    )

    const t = useAutoTranslate(appLanguage, [
        'Commanders & Castellans Crafting',
        'Configure separate crafting priorities for commanders and castellans.',
        'Offline',
        'Online',
        'Busy',
        'Ready',
        'Standby',
        'Idle',
        'Issue',
        'Running live automation for the configured commander and castellan profiles.',
        'Ready. Press Start crafting to run one manual pass.',
        'Press Start crafting to start the bot and run one manual pass.',
        'Bot is in standby. Start crafting will resume the bot and run one manual pass.',
        'Enable commander or castellan crafting, then press Start crafting.',
        'Crafting runs only when you press Start crafting.',
        'The bot reported an issue while processing equipment crafting.',
        'The game session did not answer in time. Wait for reconnect and try again.',
        'Start crafting',
        'Live names synced',
        'Live names sync issue',
        'Live names will appear after the bot syncs commanders and castellans.',
        'Last finished',
        'Crafting Scope',
        'Choose the target by live name and keep the two equipment profiles separated.',
        'Enabled',
        'Commander',
        'Commander crafting',
        'Prepare offensive crafting priorities for commanders.',
        'Commander ID',
        'Castellan',
        'Castellan crafting',
        'Prepare defensive crafting priorities for castellans.',
        'Castellan ID',
        'Commander Section',
        'Offensive priorities for commander equipment crafting.',
        'Castellan Section',
        'Defensive priorities for castellan equipment crafting.',
        'Priority',
        'Use',
        'Highest Value',
        'Not used for this rule',
        'Player effects',
        'NPC effects',
        'Melee units strength',
        'Range units strength',
        'Courtyard strength',
        'Front units limit',
        'Flank units limit',
        'Wall units limit',
        'Travel speed',
        'Later army detection',
        'Front strength',
        'Flanks strength',
        'Universal combat strength',
        'Must have Shield-maiden support in courtyard',
        'Must have additional waves',
        'Defending main castle strength',
        'Earlier army detection',
        'Defending outpost/landmark strength'
    ])

    const normalizedRuntimeStatus = runtimeStatus && typeof runtimeStatus === 'object' ? runtimeStatus : {}
    const activeScopeCount = (config.commander.enabled ? 1 : 0) + (config.castellan.enabled ? 1 : 0)
    const isBotStandby = Boolean(botStandbyActive)
    const standbyReason = String(botStandbyReason ?? '').trim()
    const hasRuntimeError = Boolean(normalizedRuntimeStatus.lastError)
    const liveTargets = normalizedRuntimeStatus.targets && typeof normalizedRuntimeStatus.targets === 'object' ?
        normalizedRuntimeStatus.targets :
        {}
    const castellanFallbackOptions = React.useMemo(
        () => buildCastellanCastleFallbackOptions(availableCastleTargets),
        [availableCastleTargets]
    )

    const buildLordOptions = (scopeKey, fallbackIds, optionLabelPrefix) => {
        const liveOptions = Array.isArray(liveTargets[scopeKey]) ? liveTargets[scopeKey] : []
        if (scopeKey === 'castellan') {
            const mergedCastellanOptions = mergeCastellanOptionsWithCastleTargets(
                liveOptions,
                availableCastleTargets
            )
            if (mergedCastellanOptions.length > 0)
                return mergedCastellanOptions
        }

        if (liveOptions.length === 0)
            return fallbackIds

        return liveOptions
            .map(option => ({
                value: String(option?.selectedId ?? option?.value ?? '').trim(),
                label: String(option?.label ?? option?.name ?? '').trim()
            }))
            .filter(option => option.value)
    }

    const commanderOptions = buildLordOptions('commander', COMMANDER_ID_OPTIONS, t('Commander'))
    const castellanOptions = castellanFallbackOptions.length > 0 ?
        buildLordOptions('castellan', castellanFallbackOptions, t('Castellan')) :
        buildLordOptions('castellan', CASTELLAN_ID_OPTIONS, t('Castellan'))
    const liveNameCount =
        (Array.isArray(liveTargets.commander) ? liveTargets.commander.length : 0) +
        (Array.isArray(liveTargets.castellan) ? liveTargets.castellan.length : 0)

    let statusLabel = t('Idle')
    let statusColor = 'default'

    if (isBotStandby) {
        statusLabel = t('Standby')
        statusColor = 'warning'
    }
    else if (hasRuntimeError) {
        statusLabel = t('Issue')
        statusColor = 'error'
    }
    else if (botRunning && Boolean(normalizedRuntimeStatus.online)) {
        statusLabel = normalizedRuntimeStatus.busy ? t('Busy') : t('Online')
        statusColor = 'success'
    }
    else if (activeScopeCount > 0) {
        statusLabel = t('Ready')
        statusColor = 'info'
    }

    const statusMessage = isBotStandby ?
        `${t('Bot is in standby. Start crafting will resume the bot and run one manual pass.')}${standbyReason ? ` ${standbyReason}` : ''}` :
        hasRuntimeError ?
        (String(normalizedRuntimeStatus.lastError).trim() === 'TIMED_OUT' ?
            t('The game session did not answer in time. Wait for reconnect and try again.') :
            `${t('The bot reported an issue while processing equipment crafting.')} ${normalizedRuntimeStatus.lastError}`) :
        (normalizedRuntimeStatus.message ?
            normalizedRuntimeStatus.message :
            (activeScopeCount > 0 ?
                (botRunning ?
                    t('Ready. Press Start crafting to run one manual pass.') :
                    t('Press Start crafting to start the bot and run one manual pass.')) :
                t('Enable commander or castellan crafting, then press Start crafting.')))

    const formatRuntimeTime = value => {
        const timestamp = Number(value)
        if (!Number.isFinite(timestamp) || timestamp <= 0)
            return ''

        return new Date(timestamp).toLocaleString(undefined, {
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    const targetSyncLabel = normalizedRuntimeStatus.lastTargetsError ?
        `${t('Live names sync issue')}: ${normalizedRuntimeStatus.lastTargetsError}` :
        (normalizedRuntimeStatus.lastTargetsSyncAt ?
            `${t('Live names synced')}: ${formatRuntimeTime(normalizedRuntimeStatus.lastTargetsSyncAt)}` :
            t('Live names will appear after the bot syncs commanders and castellans.'))
    const lastFinishedLabel = formatRuntimeTime(normalizedRuntimeStatus.lastFinishedAt)
    const canStartCrafting = activeScopeCount > 0 && !normalizedRuntimeStatus.busy

    const updateCrafting = React.useCallback((producer, afterChange) => {
        const nextValue = cloneValue(value ?? {})
        const nextCrafting = normalizeCommandersCastellansCrafting(nextValue.commandersCastellansCrafting)

        producer(nextCrafting)

        nextValue.commandersCastellansCrafting = nextCrafting
        onChange?.(nextValue)
        afterChange?.(nextValue)
    }, [onChange, value])

    const requestManualStart = () => {
        if (!canStartCrafting)
            return

        updateCrafting(nextCrafting => {
            nextCrafting.runRequestedAt = Date.now()
        }, nextValue => {
            onManualStart?.(nextValue)
        })
    }

    const updateScopeEnabled = (scopeKey, enabled) => {
        updateCrafting(nextCrafting => {
            nextCrafting[scopeKey].enabled = enabled
        })
    }

    const updateScopeId = (scopeKey, selectedId) => {
        updateCrafting(nextCrafting => {
            nextCrafting[scopeKey].selectedId = selectedId
        })
    }

    const updateScopeEffect = (scopeKey, effectKey, checked) => {
        updateCrafting(nextCrafting => {
            const effectValue = nextCrafting[scopeKey].effects[effectKey]

            if (effectValue && typeof effectValue === 'object') {
                effectValue.enabled = checked
                if (!checked)
                    effectValue.highestValue = false
                return
            }

            nextCrafting[scopeKey].effects[effectKey] = checked
        })
    }

    const updateScopeHighestValue = (scopeKey, effectKey, checked) => {
        updateCrafting(nextCrafting => {
            const effectValue = nextCrafting[scopeKey].effects[effectKey]

            if (!effectValue || typeof effectValue !== 'object')
                return

            effectValue.highestValue = checked
        })
    }

    return (
        <Box sx={{ pt: 0.25 }}>
            <Stack spacing={2.5}>
                <Paper
                    sx={{
                        p: 2.4,
                        borderRadius: 2.8,
                        background: 'linear-gradient(180deg, rgba(36,51,80,0.58) 0%, rgba(20,31,52,0.52) 100%)',
                        border: '1px solid rgba(255,255,255,0.08)'
                    }}
                >
                    <Stack spacing={1.5}>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1.2}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                        >
                            <Stack direction="row" spacing={1.25} alignItems="center">
                                <Inventory2RoundedIcon sx={{ color: 'primary.light' }} />
                                <Box>
                                    <Typography variant="h5" sx={{ fontSize: '1.12rem', fontWeight: 800 }}>
                                        {t('Commanders & Castellans Crafting')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                                        {t('Configure separate crafting priorities for commanders and castellans.')}
                                    </Typography>
                                </Box>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                                <Chip
                                    label={statusLabel}
                                    color={statusColor}
                                    variant="outlined"
                                    sx={{ fontWeight: 700 }}
                                />
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<PlayArrowRoundedIcon />}
                                    disabled={!canStartCrafting}
                                    onClick={requestManualStart}
                                    sx={{
                                        borderRadius: 1,
                                        fontWeight: 800,
                                        whiteSpace: 'nowrap',
                                        minWidth: 132
                                    }}
                                >
                                    {t('Start crafting')}
                                </Button>
                            </Stack>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="flex-start">
                            <InfoOutlinedIcon sx={{ mt: '2px', fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
                                {statusMessage}
                            </Typography>
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                            <Chip
                                size="small"
                                label={`${targetSyncLabel}${liveNameCount > 0 ? ` (${liveNameCount})` : ''}`}
                                variant="outlined"
                                sx={{ justifyContent: 'flex-start' }}
                            />
                            {lastFinishedLabel && (
                                <Chip
                                    size="small"
                                    color="success"
                                    label={`${t('Last finished')}: ${lastFinishedLabel}`}
                                    variant="outlined"
                                    sx={{ justifyContent: 'flex-start' }}
                                />
                            )}
                        </Stack>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {t('Crafting runs only when you press Start crafting.')}
                        </Typography>
                    </Stack>
                </Paper>

                <Paper
                    sx={{
                        p: 2.3,
                        borderRadius: 2.8,
                        backgroundColor: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.07)'
                    }}
                >
                    <Stack spacing={2}>
                        <SectionHeader
                            title={t('Crafting Scope')}
                            description={t('Choose the target by live name and keep the two equipment profiles separated.')}
                        />

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <ScopeCard
                                    title={t('Commander crafting')}
                                    description={t('Prepare offensive crafting priorities for commanders.')}
                                    enabled={config.commander.enabled}
                                    selectedId={config.commander.selectedId}
                                    onToggle={enabled => updateScopeEnabled('commander', enabled)}
                                    onSelectChange={selectedId => updateScopeId('commander', selectedId)}
                                    idLabel={t('Commander ID')}
                                    optionLabelPrefix={t('Commander')}
                                    enabledLabel={t('Enabled')}
                                    options={commanderOptions}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ScopeCard
                                    title={t('Castellan crafting')}
                                    description={t('Prepare defensive crafting priorities for castellans.')}
                                    enabled={config.castellan.enabled}
                                    selectedId={config.castellan.selectedId}
                                    onToggle={enabled => updateScopeEnabled('castellan', enabled)}
                                    onSelectChange={selectedId => updateScopeId('castellan', selectedId)}
                                    idLabel={t('Castellan ID')}
                                    optionLabelPrefix={t('Castellan')}
                                    enabledLabel={t('Enabled')}
                                    options={castellanOptions}
                                />
                            </Grid>
                        </Grid>
                    </Stack>
                </Paper>

                <Grid container spacing={2}>
                    <Grid item xs={12} xl={6}>
                        <EffectMatrix
                            title={t('Commander Section')}
                            description={t('Offensive priorities for commander equipment crafting.')}
                            rows={COMMANDER_EFFECTS}
                            scopeEffects={config.commander.effects}
                            onToggleEffect={(effectKey, checked) => updateScopeEffect('commander', effectKey, checked)}
                            onToggleHighest={(effectKey, checked) => updateScopeHighestValue('commander', effectKey, checked)}
                            t={t}
                        />
                    </Grid>
                    <Grid item xs={12} xl={6}>
                        <EffectMatrix
                            title={t('Castellan Section')}
                            description={t('Defensive priorities for castellan equipment crafting.')}
                            rows={CASTELLAN_EFFECTS}
                            scopeEffects={config.castellan.effects}
                            onToggleEffect={(effectKey, checked) => updateScopeEffect('castellan', effectKey, checked)}
                            onToggleHighest={(effectKey, checked) => updateScopeHighestValue('castellan', effectKey, checked)}
                            t={t}
                        />
                    </Grid>
                </Grid>
            </Stack>
        </Box>
    )
}
